import React, { useState } from "react";
import FunderShell from "./FunderShell";
import { SectionCard, ScoreTriad } from "./FunderUI";
import { getPreprints } from "../../service/FunderPortalService";

// Color map for the computed radar labels.
const LABEL_META = {
    Investigate: { bg: "var(--success-bg)", color: "var(--success)" },
    Watch: { bg: "var(--info-bg)", color: "var(--info)" },
    "Needs validation": { bg: "var(--warning-bg)", color: "var(--warning)" },
    "Potential seed area": { bg: "var(--surface-4)", color: "var(--ink-450)" },
};

export default function FunderPreprints() {
    return (
        <FunderShell activePage="Preprint Radar">
            {(callId, call) => <Inner callId={callId} call={call} />}
        </FunderShell>
    );
}

function Inner({ callId, call }) {
    const preprints = [...getPreprints(callId)].sort((a, b) => b.momentum - a.momentum);
    const [selectedId, setSelectedId] = useState(preprints[0] ? preprints[0].id : null);
    const selected = preprints.find((p) => p.id === selectedId) || preprints[0] || null;

    // Group by cluster for the momentum column.
    const byCluster = {};
    preprints.forEach((p) => {
        (byCluster[p.cluster] = byCluster[p.cluster] || []).push(p);
    });
    const clusters = Object.entries(byCluster)
        .map(([name, list]) => ({
            name,
            count: list.length,
            momentum: Math.round(list.reduce((s, p) => s + p.momentum, 0) / list.length),
        }))
        .sort((a, b) => b.momentum - a.momentum);

    return (
        <div className="funder-section-stack">
            <div className="funder-page-header">
                <div className="funder-overline">{call.funder} · {call.scheme}</div>
                <h1 className="funder-h1">Preprint Radar</h1>
                <p className="funder-subtitle">
                    High-signal preprints adjacent to this call's research areas that are not yet funded —
                    early indicators of momentum, not achieved impact.
                </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, alignItems: "start" }}>
                {/* Ranked list */}
                <SectionCard kicker="Ranked by momentum" title="Unfunded preprints">
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {preprints.map((pp) => {
                            const lm = LABEL_META[pp.label] || LABEL_META.Watch;
                            const active = selected && pp.id === selected.id;
                            return (
                                <div
                                    key={pp.id}
                                    className="funder-pp-row"
                                    onClick={() => setSelectedId(pp.id)}
                                    style={{
                                        border: `1px solid ${active ? "var(--series-blue)" : "var(--border)"}`,
                                        borderRadius: 12, padding: 12, cursor: "pointer",
                                        background: active ? "var(--surface-2)" : "var(--surface)",
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)", lineHeight: 1.3, minWidth: 0 }}>{pp.title}</span>
                                        <span className="status-badge" style={{ background: lm.bg, color: lm.color, flexShrink: 0, height: "fit-content" }}>{pp.label}</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: "var(--ink-400)", margin: "4px 0 8px" }}>{pp.authors} · {pp.source} · {pp.posted}</div>
                                    <ScoreTriad p={pp} size="sm" gap={12} />
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                                        <span style={{ fontSize: 11, color: "var(--ink-350)", width: 66 }}>Momentum</span>
                                        <div className="funder-bar-track" style={{ flex: 1 }}>
                                            <div className="funder-bar-fill" style={{ width: `${pp.momentum}%`, background: "var(--series-blue)" }} />
                                        </div>
                                        <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--series-blue)", width: 24, textAlign: "right" }}>{pp.momentum}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </SectionCard>

                {/* Cluster momentum */}
                <SectionCard className="funder-pp-clusters" kicker="Momentum" title="Cluster momentum">
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {clusters.map((c) => (
                            <div key={c.name}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 5 }}>
                                    <span style={{ fontSize: 12.5, color: "var(--ink-750)" }}>{c.name}</span>
                                    {c.momentum >= 80 && <span className="funder-frontier-pill">Whitespace</span>}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <div className="funder-bar-track" style={{ flex: 1 }}>
                                        <div className="funder-bar-fill" style={{ width: `${c.momentum}%`, background: c.momentum >= 80 ? "var(--series-green)" : "var(--series-blue)" }} />
                                    </div>
                                    <span style={{ fontSize: 11.5, color: "var(--ink-400)", width: 66, textAlign: "right" }}>{c.momentum} · {c.count} pp</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </SectionCard>

                {/* Detail */}
                <SectionCard className="funder-pp-detail" kicker="Detail" title="Selected preprint">
                    {selected ? (
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 650, color: "var(--ink-900)", lineHeight: 1.35 }}>{selected.title}</div>
                            <div style={{ fontSize: 12.5, color: "var(--ink-400)", margin: "6px 0 12px" }}>{selected.authors} · {selected.source} · {selected.posted}</div>
                            <ScoreTriad p={selected} />
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "14px 0" }}>
                                {selected.signals.map((s, i) => (
                                    <span key={i} className="funder-tag">{s}</span>
                                ))}
                            </div>
                            <div style={{ fontSize: 13, color: "var(--ink-600)", lineHeight: 1.55 }}>{selected.abstract}</div>
                            <div style={{ fontSize: 12, color: "var(--ink-350)", marginTop: 12 }}>Cluster: {selected.cluster}</div>
                        </div>
                    ) : (
                        <div className="funder-empty">No preprints for this call.</div>
                    )}
                </SectionCard>
            </div>
        </div>
    );
}
